import { Moment } from 'moment';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { InitialModality, ChatChannelType } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';

export interface AgentAssistantAfterCallWorkStateModel {
  unifiedConversationId: string;
  conversationId: string;
  teamId: string;
  initialModality: InitialModality;
  channel?: ChatChannelType;
  state: UserInteractionOutcome;
  afterCallWorkSince: string;
  afterCallWorkDuration: string;
  afterCallWorkAllowToStop: boolean;
  afterCallWorkAllowToExtend: boolean;
}

export interface AgentAssistantAcwViewModel {
  unifiedConversationId: string;
  conversationId: string;
  teamId: string;
  userSessionId?: string;
  initialModality: InitialModality;
  channel?: ChatChannelType;
  state: UserInteractionOutcome;
  afterCallWorkUntil: Moment;
  afterCallWorkDuration: string;
  afterCallWorkAllowToStop: boolean;
  afterCallWorkAllowToExtend: boolean;
}

export interface IUserResponsibilityProfile {
  id: string;
  name: string;
  active: boolean;
  isDuty: boolean;
}

export enum UserInteractionOutcome {
  Unknown = 'Unknown',
  Connected = 'Connected',
  Incoming = 'Incoming',
  Ongoing = 'Ongoing',
  Transferring = 'Transferring',
  Accepted = 'Accepted',
  Declined = 'Declined',
  Rona = 'Rona',
  Abandoned = 'Abandoned',
  InAfterCallWork = 'InAfterCallWork',
  InExtendedAfterCallWork = 'InExtendedAfterCallWork',
  Parked = 'Parked',
  PutOnHold = 'PutOnHold',
  DialingOut = 'DialingOut',
  DestinationAccepted = 'DestinationAccepted',
  DestinationDeclined = 'DestinationDeclined',
  DestinationNotReached = 'DestinationNotReached',
  OutboundUserNotAccepted = 'OutboundUserNotAccepted',
  OutboundUserAborted = 'OutboundUserAborted',
  MarkedAsHandledByUser = 'MarkedAsHandledByUser',
  Transferred = 'Transferred',
  TransferFailed = 'TransferFailed',
  ConsultedTransferred = 'ConsultedTransferred',
}

export const onGoingStatesPriority: Partial<Record<UserInteractionOutcome, number>> = {
  [UserInteractionOutcome.Incoming]: 0,
  [UserInteractionOutcome.Ongoing]: 1,
  [UserInteractionOutcome.DialingOut]: 2,
  [UserInteractionOutcome.PutOnHold]: 3,
  [UserInteractionOutcome.Parked]: 4,
  [UserInteractionOutcome.Transferring]: 5,
};

export const UserInteractionOutcomeTranslations: Record<UserInteractionOutcome, TranslationKeys> = {
  [UserInteractionOutcome.Accepted]: TranslationKeys.GeneralUserInteractionOutcomeAccepted,
  [UserInteractionOutcome.Connected]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [UserInteractionOutcome.Ongoing]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [UserInteractionOutcome.Parked]: TranslationKeys.GeneralUserInteractionOutcomeParked,
  [UserInteractionOutcome.PutOnHold]: TranslationKeys.GeneralUserInteractionOutcomePutOnHold,
  [UserInteractionOutcome.Transferring]: TranslationKeys.GeneralUserInteractionOutcomeTransferring,
  [UserInteractionOutcome.Abandoned]: TranslationKeys.GeneralUserInteractionOutcomeCancelled,
  [UserInteractionOutcome.Declined]: TranslationKeys.GeneralUserInteractionOutcomeDeclined,
  [UserInteractionOutcome.Incoming]: TranslationKeys.GeneralUserInteractionOutcomeIncoming,
  [UserInteractionOutcome.Rona]: TranslationKeys.GeneralUserInteractionOutcomeRona,
  [UserInteractionOutcome.Unknown]: TranslationKeys.GeneralUserInteractionOutcomeUnknown,
  [UserInteractionOutcome.InAfterCallWork]: TranslationKeys.GeneralUserInteractionOutcomeInAfterCallWork,
  [UserInteractionOutcome.InExtendedAfterCallWork]: TranslationKeys.GeneralUserInteractionOutcomeInExtendedAfterCallWork,
  [UserInteractionOutcome.DialingOut]: TranslationKeys.GeneralUserInteractionOutcomeDialingOut,
  [UserInteractionOutcome.OutboundUserNotAccepted]: TranslationKeys.GeneralUserInteractionOutcomeNotAccepted,
  [UserInteractionOutcome.OutboundUserAborted]: TranslationKeys.GeneralUserInteractionOutcomeUserAborted,
  [UserInteractionOutcome.DestinationDeclined]: TranslationKeys.GeneralUserInteractionOutcomeDestinationDeclined,
  [UserInteractionOutcome.DestinationNotReached]: TranslationKeys.GeneralUserInteractionOutcomeNotReached,
  [UserInteractionOutcome.DestinationAccepted]: TranslationKeys.GeneralUserInteractionOutcomeDestinationAccepted,
  [UserInteractionOutcome.MarkedAsHandledByUser]: TranslationKeys.GeneralUserInteractionOutcomeMarkedAsHandled,
  [UserInteractionOutcome.TransferFailed]: TranslationKeys.GeneralUserInteractionOutcomeTransferFailed,
  [UserInteractionOutcome.Transferred]: TranslationKeys.GeneralUserInteractionOutcomeTransferred,
  [UserInteractionOutcome.ConsultedTransferred]: TranslationKeys.GeneralUserInteractionOutcomeConsultedTransferred,
};

export function isParked(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.Parked].includes(state);
}

export function isAfterCallWork(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.InAfterCallWork, UserInteractionOutcome.InExtendedAfterCallWork].includes(state);
}

export function isOngoing(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.Connected, UserInteractionOutcome.Incoming].includes(state) || isAfterCallWork(state) || isParked(state);
}

export function isDestinationNotReached(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.DestinationNotReached].includes(state);
}

export function isTranscriptionOngoing(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.Connected].includes(state);
}

export function isUnknown(state: UserInteractionOutcome): boolean {
  return [UserInteractionOutcome.Unknown].includes(state);
}

export function isBlockingStateTaskForUnparkPresent(subStates: UserInteractionOutcome[]): boolean {
  return subStates.some((state) =>
    [
      UserInteractionOutcome.Incoming,
      UserInteractionOutcome.Ongoing,
      UserInteractionOutcome.PutOnHold,
      UserInteractionOutcome.DialingOut,
      UserInteractionOutcome.Transferring,
    ].includes(state),
  );
}
